// Pure display helpers for the dashboard route.
//
// `eslint --max-warnings=0` enforces `react-refresh/only-export-components`, and
// the rule is right: a file that exports both a component and a plain function
// makes Vite's fast-refresh fall back to a full reload, so editing the hero
// costs a state reset. `cycleProgress` and the pair resolvers below are all
// plain functions, so they live here instead of inside the `.tsx` files.
//
// Same split as `components/ui/icons.ts` and `components/brand/brandCopy.ts`:
// a `.tsx` file whose main export is a component is a better place to look for
// a component, and the logic beside it stays testable without rendering.
//
// Mirrors `moduleMeta.ts` / `moodCodes.ts` in this same directory: pure logic
// next to the components that use them, with its own unit tests.

import type { DashboardPairMember } from "@/lib/api/dashboard";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);

/**
 * How far through **its own cycle** an anniversary is — `ADR-0138`.
 *
 * An anniversary recurs, so the meaningful denominator is one cycle, not one
 * calendar year: from the previous occurrence to the next. `2027-07-23`, viewed
 * on `2026-10-08`, is 77 days into a 365-day cycle — about 21%.
 *
 * That is a mostly-empty ring most of the time, and that is the honest reading:
 * 288 days out means the cycle is two-tenths done. A nearly-full ring would have
 * been prettier and would have said the opposite.
 *
 * Both sides are compared as **UTC midnight**. `new Date("2027-07-23")` parses
 * as UTC while `getFullYear()` is local, so mixing them shifts the answer by a
 * day for anyone west of Greenwich — the ring would read 21% one day early or
 * late depending on where the phone is.
 *
 * Returns `null` when there is nothing to compute from; the caller then omits
 * the ring rather than rendering a full or empty one as if it meant something.
 */
export function cycleProgress(
  date: string | null | undefined,
  daysUntil: number | undefined,
  now: Date = new Date(),
): number | null {
  const parsed = date?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (parsed) {
    const y = Number(parsed[1]);
    const m = Number(parsed[2]) - 1;
    const d = Number(parsed[3]);
    const nextUtc = Date.UTC(y, m, d);
    const prevUtc = Date.UTC(y - 1, m, d);
    const span = nextUtc - prevUtc;
    if (span > 0) {
      const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      return clamp01((todayUtc - prevUtc) / span);
    }
  }
  // No usable date: the countdown alone still implies a cycle length. Less exact,
  // and it says so in the code rather than pretending the date was parsed.
  if (typeof daysUntil === "number" && Number.isFinite(daysUntil)) {
    return clamp01(1 - daysUntil / 365);
  }
  return null;
}

/**
 * W57.0 — **who is who, relative to the viewer**.
 *
 * ### Why this function exists
 *
 * The dashboard payload carries `pair.members` ordered **`(owner, partner)`**,
 * not `(viewer, partner)` — `backend/app/modules/dashboard/service/pair_block.py`
 * sorts by `0 if m.role == "owner" else 1`. The owner is a property of the
 * pair; the viewer is a property of the session, and the two are different
 * people for half the pair.
 *
 * `PairHeroCard` read `members[0]` as 「我」 and `members[1]` as 「對方」, so a
 * **partner** who logged in saw the owner's photograph on the left under 「我」
 * and their own on the right under 「對方」 — the two labels swapped, along with
 * the faces. The unit test did not catch it because the fixture happened to
 * have the viewer as the owner.
 *
 * So the ordering is resolved **here**, against the viewer's id, and both the
 * hero and the activity feed ask this function rather than indexing an array.
 */
export interface PairSides {
  /** The signed-in member, or `null` when the payload has no members. */
  me: DashboardPairMember | null;
  /** The other member of the pair. */
  partner: DashboardPairMember | null;
}

/**
 * Resolve the pair's two members against the viewer's id.
 *
 * The fallback is the payload's own `(owner, partner)` order, and it is used
 * in exactly two cases: there is no viewer id at all, or the viewer's id is
 * not among the members. The second is data drift, and guessing which of two
 * strangers is "the partner" is exactly the kind of guess `ADR-0079` refuses.
 */
export function resolvePairSides(
  members: readonly DashboardPairMember[],
  currentUserId: string | null,
): PairSides {
  if (members.length === 0) return { me: null, partner: null };

  if (currentUserId) {
    const me = members.find((m) => m.user_id === currentUserId) ?? null;
    if (me) {
      const partner = members.find((m) => m.user_id !== currentUserId) ?? null;
      return { me, partner };
    }
  }

  return { me: members[0] ?? null, partner: members[1] ?? null };
}

/**
 * W57.0 — the **name** of the person who wrote a feed row.
 *
 * `null` means **render no chip**, and it has three distinct causes that must
 * not be collapsed:
 *
 *   - the row is system-generated (`actor_id: null`) — the listener-created
 *     anchor anniversary, which nobody wrote;
 *   - the actor is the viewer → 「我」;
 *   - the actor is neither the viewer nor the partner — drift, and naming
 *     somebody here would be a guess (`ADR-0079`).
 *
 * The partner is named by their **real display name**, not 「對方」: the feed
 * is read as a record of what two people did, and 「對方」 in a column of
 * several rows tells the reader nothing they could not already tell from the
 * avatar — whereas the name tells them *which* person.
 */
export function actorName(
  actorId: string | null,
  sides: PairSides,
  currentUserId: string | null,
): string | null {
  if (!actorId) return null;
  if (currentUserId && actorId === currentUserId) return DASHBOARD_I18N.modules.actorMe;
  const partner = sides.partner;
  if (partner && actorId === partner.user_id) return partner.display_name;
  return null;
}
